import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { nanoid } from 'nanoid';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addSeat, clearSeats, removeSeat, setOpenDetails } from '../../redux/slices/reservationsCartSlice';
import { useTheme, useMediaQuery, Box } from '@mui/material';
import { SeatsioEventManager } from '@seatsio/seatsio-react';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import WarningDialog from '../WarningDialog';
import DesktopDrawer from './DesktopDrawer';
import MobileDrawer from './MobileDrawer.js';
import DetailsDialog from './DetailsDialog';
import ScheduleIcon from '@mui/icons-material/Schedule';
import api from './../../api.js';

import { getReservationSelection, createReservationSelectability } from '../../utils/reservationUtils';

const drawerWidth = 340;

const EVENT_INITIAL_STATE = {
  title: '-',
  location: '-',
  city: '-',
  date: '',
  scan_code: '',
  is_one_plus_one_free: false,
  is_three_plus_one_free: false,
  premiere: false,
  national_premiere: false,
  second_representation: false,
  platform_comission: 0,
  seat_price: 0,
  comission_cash_per_seat: false,
  notification_sms_price: 0,
  notifiction_email_price: 0,
  status: 'SELLING',
  can_delete: false,
  index_for_search: true,
};

function ReservationsDialog({ open, onClose, reservation, eventId }) {
  const { t } = useTranslation();

  const chartRef = useRef(null);
  const sessionRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();

  const [statusChanged, setStatusChanged] = useState(false);
  const [event, setEvent] = useState(JSON.parse(JSON.stringify(EVENT_INITIAL_STATE)));
  const [categoryPricings, setCategoryPricings] = useState([]);

  // eslint-disable-next-line no-unused-vars
  const [isEventPending, setEventPending] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [isPricingsPending, setPricingsPending] = useState(true);

  const currentSeats = useSelector((state) => state.reservationsCart.seats);
  const openDetails = useSelector((state) => state.reservationsCart.openDetails);

  const preTitle = useMemo(
    () => (!reservation ? t('Create Reservation') : t(`Update Reservation`) + ` #${reservation.id}`),
    [reservation, t]
  );

  const seats = useMemo(() => {
    if (!reservation) return [];
    const { cart } = reservation;
    const { seats } = cart; //JSON.parse(cart);
    return seats;
  }, [reservation]);

  useEffect(() => {
    if (open && (eventId || reservation)) {
      const actualEventId = eventId || reservation.events_id;
      const eventsPromise = api.get(`/events/${actualEventId}`).then((res) => {
        if (res.data && res.data.data && res.data.data.length > 0) {
          setEvent(res.data.data[0]);
        }
        setEventPending(false);
      });
      const pricingsPromise = api.get(`/events/${actualEventId}/categoryPricings?sales_channel=cashier`).then((res) => {
        if (res.data && res.data.data) {
          setCategoryPricings(res.data.data);
        }
        setPricingsPending(false);
      });

      Promise.all([eventsPromise, pricingsPromise]).catch((err) => {
        console.log('Error fetching data ', err);
        toast.error(t('An error occured please try again!'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      });
    }
  }, [eventId, reservation, open, setPricingsPending, setEventPending, t]);

  useEffect(() => {
    if (!open) {
      dispatch(clearSeats());
      sessionRef.current = null;
      setEvent(JSON.parse(JSON.stringify(EVENT_INITIAL_STATE)));
      setEventPending(true);
      setPricingsPending(true);
      setCategoryPricings([]);
      setStatusChanged(false);
    }
  }, [dispatch, open]);

  const ticketTypes = useMemo(() => {
    const ticketsByCatId = categoryPricings.reduce(
      (acc, { categories_id, ...rest }) => ({ ...acc, [categories_id]: [...(acc[categories_id] || []), rest] }),
      {}
    );

    return Object.keys(ticketsByCatId).map((categoryId) => {
      const ticketTypes = ticketsByCatId[categoryId];

      if (ticketTypes.length === 1) {
        return { category: categoryId, ticketTypes: [] };
      } else {
        return {
          category: categoryId,
          ticketTypes: ticketTypes.map(({ type_id, type_label }) => {
            return { ticketType: String(type_id), label: type_label };
          }),
        };
      }
    });
  }, [categoryPricings]);

  const selectedObjects = useMemo(
    () => (ticketTypes.length ? getReservationSelection(seats, ticketTypes) : []),
    [seats, ticketTypes]
  );

  const isObjectSelectable = useMemo(() => createReservationSelectability(seats), [seats]);

  const onChartRendered = (chart) => {
    chartRef.current = chart;
    if (!sessionRef.current) sessionRef.current = nanoid();
  };

  const onRemoveAll = () => {
    if (!chartRef || !chartRef.current) return;
    chartRef.current.clearSelection();
  };

  const onRemove = (seatId, ticketType) => {
    if (!chartRef || !chartRef.current) return;
    chartRef.current.deselectObjects([
      { label: seatId, ...(ticketType ? { ticketType: String(ticketType) } : {}), amount: 1 },
    ]);
  };

  const onObjectSelected = (selectedObj, selectedTicketType) => {
    const {
      seatId,
      labels,
      category: { key, label, color },
    } = selectedObj;

    const { section, parent: row, own: seat } = labels;

    const priceInfos = categoryPricings.filter((pricing) => Number(pricing.categories_id) === Number(key));

    const priceInfo = selectedTicketType
      ? priceInfos.filter((pricing) => String(pricing.type_id) === selectedTicketType.ticketType)[0]
      : priceInfos[0];

    const seatObj = {
      id: seatId,
      objType: selectedObj.objectType === 'GeneralAdmissionArea' ? 'generalAdmission' : selectedObj.objectType,
      singleType: !selectedTicketType,
      typeId: priceInfo.type_id,
      typeLabel: priceInfo.type_label,
      isDefaultType: priceInfo.is_default_type,
      section,
      row,
      seat,
      categoryId: key,
      categoryLabel: label,
      categoryColor: color,
    };

    dispatch(addSeat(seatObj));
  };

  const onObjectDeselected = (deselectedObj, deselectedTicketType) => {
    const { seatId } = deselectedObj;

    const seatObj = {
      id: seatId,
      ...(deselectedTicketType ? { typeId: deselectedTicketType.ticketType } : {}),
    };

    dispatch(removeSeat(seatObj));
  };

  const onObjectStatusChanged = (obj) => {
    if (obj.extraData && obj.extraData.session === sessionRef.current) return;

    if (obj.objectType !== 'GeneralAdmissionArea' && obj.status !== 'free' && obj.selected) {
      dispatch(setOpenDetails(false));
      setStatusChanged(true);
    }
  };

  const onContinue = () => {
    dispatch(setOpenDetails(true));
  };

  const onCancel = () => {
    onClose();
  };

  const drawerProps = {
    drawerWidth,
    seats: currentSeats,
    onRemove,
    onRemoveAll,
    onContinue,
    onCancel,
  };

  return (
    <Dialog fullScreen={true} open={open} onClose={onClose}>
      <Box sx={{ width: `calc(100% - ${!isMobile ? drawerWidth : 0}px)` }}>
        <DialogTitle gutterBottom>
          <Box display="flex" alignItems="center">
            <ScheduleIcon sx={{ marginRight: 1 }} />
            {preTitle}
          </Box>
        </DialogTitle>
        <Divider />
      </Box>
      <DialogContent sx={{ padding: 0 }}>
        <Box style={{ height: '100%', width: `calc(100% - ${!isMobile ? drawerWidth : 0}px)` }}>
          {event && event.seatsio_event_id && !isPricingsPending && (
            <SeatsioEventManager
              onObjectStatusChanged={onObjectStatusChanged}
              secretKey={process.env.REACT_APP_SEATSIO_PRIVATE_WORKSPACE_KEY}
              event={event.seatsio_event_id}
              mode="select"
              showFullScreenButton={false}
              isObjectSelectable={isObjectSelectable}
              viewSettingsDefaults={{ showSeatLabels: true, showRowLabels: true }}
              region={process.env.REACT_APP_SEATSIO_REGION}
              onChartRendered={onChartRendered}
              ticketTypes={ticketTypes}
              onObjectSelected={onObjectSelected}
              onObjectDeselected={onObjectDeselected}
              selectedObjects={selectedObjects}
            />
          )}
        </Box>
        {!isMobile ? <DesktopDrawer {...drawerProps} /> : <MobileDrawer {...drawerProps} />}
        <DetailsDialog
          isEventScope={!!eventId}
          open={openDetails}
          onClose={(_, reason) => {
            if (reason !== 'backdropClick') dispatch(setOpenDetails(false));
          }}
          onSaveSuccess={() => {
            dispatch(setOpenDetails(false));
            onClose();
          }}
          seats={currentSeats}
          session={sessionRef}
          event={event}
          reservation={reservation}
        />
      </DialogContent>
      <WarningDialog
        open={statusChanged}
        title={t('Warning')}
        content={t('One or more selected seats became unavailable!')}
        onConfirm={() => setStatusChanged(false)}
      />
    </Dialog>
  );
}

export default ReservationsDialog;
